import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatMoney, formatDate } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { usePeriod } from '@/app/PeriodContext';

type Origin = 'realizado' | 'contratado' | 'recorrente' | 'estimado';

type Line = {
  date: string;
  origin: Origin;
  origin_detail: string;
  type: 'receita' | 'despesa';
  description: string;
  category_name: string | null;
  amount: MoneyString;
  source_type: string;
  source_id: number;
  confidence: number;
};

type Day = {
  date: string;
  inflow: MoneyString;
  outflow: MoneyString;
  balance: MoneyString;
  line_count: number;
  lines: Line[];
};

type Forecast = {
  period: { from: string; to: string };
  horizon_days: number;
  opening_balance: MoneyString;
  closing_balance: MoneyString;
  by_origin: Record<'contratado' | 'recorrente' | 'estimado', MoneyString>;
  alerts: {
    negative_balance_date: string | null;
    worst_balance: MoneyString;
    worst_balance_date: string | null;
    working_capital_need: MoneyString;
    available_to_invest: MoneyString;
  };
  days: Day[];
  line_count: number;
  estimation_note: string;
};

/**
 * Tratamento visual por camada — RN-101.
 *
 * Quanto menos certo o número, mais leve o traço. O usuário precisa distinguir
 * fato de estimativa sem ler legenda, mas a legenda existe também.
 */
const ORIGIN_STYLE: Record<Origin, { label: string; badge: string; bar: string; hint: string }> = {
  realizado: {
    label: 'Realizado',
    badge: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
    bar: 'bg-slate-500',
    hint: 'já aconteceu',
  },
  contratado: {
    label: 'Contratado',
    badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
    bar: 'bg-indigo-500',
    hint: 'parcela, fatura ou título com data e valor definidos',
  },
  recorrente: {
    label: 'Recorrente',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
    bar: 'bg-sky-400',
    hint: 'template ativo — pode ser cancelado',
  },
  estimado: {
    label: 'Estimado',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    bar: 'bg-amber-400',
    hint: 'média móvel do histórico — nunca gerado por IA',
  },
};

const SCENARIOS = [
  { value: 'conservador', label: 'Conservador', hint: 'receita −15%, inadimplência 10%, atraso +10 dias' },
  { value: 'realista', label: 'Realista', hint: 'histórico puro, sem ajuste' },
  { value: 'otimista', label: 'Otimista', hint: 'receita +10%, despesa −5%' },
];

export function ForecastPage({ selectedIds }: { selectedIds: number[] }) {
  const { period } = usePeriod();

  /**
   * `horizon` em dias, ou 'filtro' para seguir a barra superior — RF-PRE-02.
   *
   * A projeção SEMPRE começa hoje: o saldo de partida é o de hoje, e o passado
   * já aconteceu — está no extrato, não em previsão. O que o filtro define é
   * onde a janela TERMINA.
   */
  const [horizon, setHorizon] = useState<number | 'filtro'>(90);
  const [scenario, setScenario] = useState('realista');
  const [openDay, setOpenDay] = useState<string | null>(null);

  const hoje = new Date().toISOString().slice(0, 10);

  /**
   * Só dá para seguir o filtro se ele TERMINAR no futuro.
   *
   * "Mês passado" fecha antes de hoje: a janela teria duração negativa, e
   * projetar um período que já passou não responde nada. O botão fica
   * desabilitado dizendo por quê, em vez de sumir — sumir faria a pessoa
   * procurar o que não está lá.
   */
  const filtroServe = period.to > hoje;
  const seguindoFiltro = horizon === 'filtro' && filtroServe;

  const query = useQuery({
    queryKey: ['forecast', horizon, seguindoFiltro ? period.to : null, scenario, selectedIds],
    queryFn: () =>
      api.get<{ data: Forecast; meta: { method: string } }>(
        seguindoFiltro
          ? `/forecast?until=${period.to}&scenario=${scenario}`
          : `/forecast?horizon=${horizon === 'filtro' ? 90 : horizon}&scenario=${scenario}`,
        { entityIds: selectedIds },
      ),
  });

  if (query.isPending) return <Skeleton />;
  if (query.isError) {
    return (
      <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
        Não foi possível gerar a projeção.
      </p>
    );
  }

  const d = query.data.data;
  const goesNegative = d.alerts.negative_balance_date !== null;

  const balances = d.days.map((x) => Number(x.balance));
  const min = Math.min(...balances, Number(d.opening_balance), 0);
  const max = Math.max(...balances, Number(d.opening_balance));
  const span = max - min || 1;

  return (
    <div className="mx-auto max-w-6xl">
      <h1 className="text-xl font-semibold tracking-tight">Previsão de caixa</h1>

      {/**
        * O INÍCIO É SEMPRE HOJE; o filtro escolhe o FIM.
        *
        * A versão anterior desta tela ignorava a barra superior por completo, e
        * o texto dizia isso. O argumento estava certo pela metade: "mês passado"
        * de fato não é horizonte de previsão — mas um período personalizado que
        * termina em dezembro é, e recusá-lo junto era jogar fora o caso bom.
        */}
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Projeta sempre a partir de <strong>hoje</strong> — o passado já aconteceu e está no
        extrato. Escolha até onde projetar.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        <div className="flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
          {[30, 90, 180, 365].map((h) => (
            <button
              key={h}
              onClick={() => setHorizon(h)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                horizon === h
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400'
              }`}
            >
              {h}d
            </button>
          ))}

          <button
            onClick={() => setHorizon('filtro')}
            disabled={!filtroServe}
            title={
              filtroServe
                ? `Projeta de hoje até ${formatDate(period.to)}`
                : 'O período selecionado termina antes de hoje — não há o que projetar nele.'
            }
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
              seguindoFiltro
                ? 'bg-indigo-600 text-white'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400'
            }`}
          >
            Do filtro
          </button>
        </div>

        {seguindoFiltro && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            até {formatDate(period.to)}
          </span>
        )}

        {horizon === 'filtro' && !filtroServe && (
          <span className="text-xs text-amber-700 dark:text-amber-400">
            O período da barra termina antes de hoje — mostrando 90 dias.
          </span>
        )}

        <select
          value={scenario}
          onChange={(e) => setScenario(e.target.value)}
          className="input py-1.5 text-sm"
        >
          {SCENARIOS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <span className="text-xs text-slate-400">
          {SCENARIOS.find((s) => s.value === scenario)?.hint}
        </span>
      </div>

      {/* ---------- Alerta de caixa ---------- */}
      {goesNegative ? (
        <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/50 dark:bg-rose-950/30">
          <p className="font-semibold text-rose-900 dark:text-rose-200">
            O caixa fica negativo em {formatDate(d.alerts.negative_balance_date!)}
          </p>
          <p className="mt-1 text-sm text-rose-800/80 dark:text-rose-300/80">
            Pior saldo previsto: <strong>{formatMoney(d.alerts.worst_balance)}</strong> em{' '}
            {formatDate(d.alerts.worst_balance_date ?? '')}. Para atravessar o período sem ficar
            negativo, seriam necessários{' '}
            <strong>{formatMoney(d.alerts.working_capital_need)}</strong> de capital de giro.
          </p>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/30">
          <p className="font-semibold text-emerald-900 dark:text-emerald-200">
            O caixa não fica negativo em {d.horizon_days} dias
          </p>
          <p className="mt-1 text-sm text-emerald-800/80 dark:text-emerald-300/80">
            Menor saldo do período: <strong>{formatMoney(d.alerts.worst_balance)}</strong>
            {d.alerts.worst_balance_date && ` em ${formatDate(d.alerts.worst_balance_date)}`}.
          </p>
        </div>
      )}

      {/* ---------- Saldos ---------- */}
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Saldo hoje" value={d.opening_balance} />
        <Kpi label={`Saldo em ${d.horizon_days} dias`} value={d.closing_balance} signed />
        <Kpi label="Menor saldo" value={d.alerts.worst_balance} signed />
        <Kpi label="Sobra para investir" value={d.alerts.available_to_invest} />
      </div>

      {/* ---------- Composição por camada ---------- */}
      <h2 className="mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">
        De onde vem a projeção
      </h2>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        {(['contratado', 'recorrente', 'estimado'] as const).map((origin) => (
          <div
            key={origin}
            className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-center gap-2">
              <span className={`h-2 w-6 rounded-full ${ORIGIN_STYLE[origin].bar}`} />
              <span className="text-sm font-medium">{ORIGIN_STYLE[origin].label}</span>
            </div>
            <p
              className={`mt-1.5 font-semibold ${
                Number(d.by_origin[origin]) < 0 ? 'text-rose-700 dark:text-rose-400' : ''
              }`}
            >
              {formatMoney(d.by_origin[origin])}
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">{ORIGIN_STYLE[origin].hint}</p>
          </div>
        ))}
      </div>

      {/* A honestidade sobre o próprio método — ADR-0005 */}
      <p className="mt-3 rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
        {d.estimation_note}
      </p>

      {/* ---------- Curva ---------- */}
      <h2 className="mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Curva de saldo — clique num dia para ver o que o compõe
      </h2>

      <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {d.days.length === 0 ? (
          <p className="p-10 text-center text-sm text-slate-500 dark:text-slate-400">
            Nenhum movimento previsto no horizonte.
          </p>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {d.days.map((day) => {
                const balance = Number(day.balance);
                const pct = ((balance - min) / span) * 100;
                const isOpen = openDay === day.date;

                return (
                  <>
                    <tr
                      key={day.date}
                      onClick={() => setOpenDay(isOpen ? null : day.date)}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="whitespace-nowrap px-3 py-2 font-medium">
                        {formatDate(day.date)}
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-400">
                        {day.inflow !== '0.0000' && `+ ${formatMoney(day.inflow)}`}
                      </td>
                      <td className="px-3 py-2 text-right text-rose-700 dark:text-rose-400">
                        {day.outflow !== '0.0000' && `− ${formatMoney(day.outflow)}`}
                      </td>
                      <td
                        className={`whitespace-nowrap px-3 py-2 text-right font-medium ${
                          balance < 0 ? 'text-rose-700 dark:text-rose-400' : ''
                        }`}
                      >
                        {formatMoney(day.balance)}
                      </td>
                      <td className="w-40 px-3 py-2">
                        <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
                          <div
                            className={`h-full rounded-full ${
                              balance < 0 ? 'bg-rose-500' : 'bg-indigo-500'
                            }`}
                            style={{ width: `${Math.max(pct, 2)}%` }}
                          />
                        </div>
                      </td>
                      <td className="w-8 pr-3 text-right text-xs text-slate-400">
                        {isOpen ? '−' : '+'}
                      </td>
                    </tr>

                    {/* CA-91 — drill-down: cada linha diz de onde veio */}
                    {isOpen &&
                      day.lines.map((line, i) => (
                        <tr key={`${day.date}-${i}`} className="bg-slate-50 dark:bg-slate-800/40">
                          <td colSpan={2} className="py-1.5 pl-8 pr-3 text-xs">
                            <span
                              className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                ORIGIN_STYLE[line.origin].badge
                              }`}
                            >
                              {ORIGIN_STYLE[line.origin].label}
                            </span>
                            {line.description}
                            {line.category_name && (
                              <span className="ml-1.5 text-slate-400">{line.category_name}</span>
                            )}
                          </td>
                          <td
                            colSpan={2}
                            className={`py-1.5 pr-3 text-right text-xs ${
                              Number(line.amount) >= 0
                                ? 'text-emerald-700 dark:text-emerald-400'
                                : 'text-rose-700 dark:text-rose-400'
                            }`}
                          >
                            {formatMoney(line.amount)}
                          </td>
                          <td colSpan={2} className="py-1.5 pr-3 text-right text-[10px] text-slate-400">
                            {line.source_type.replace('fin_', '')} #{line.source_id}
                          </td>
                        </tr>
                      ))}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">
        {query.data.meta.method}
      </p>
    </div>
  );
}

function Kpi({ label, value, signed }: { label: string; value: MoneyString; signed?: boolean }) {
  const negative = Number(value) < 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p
        className={`mt-1.5 text-base font-semibold ${
          signed && negative ? 'text-rose-700 dark:text-rose-400' : ''
        }`}
      >
        {formatMoney(value)}
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse">
      <div className="h-6 w-44 rounded bg-slate-200 dark:bg-slate-800" />
      <div className="mt-5 h-20 rounded-xl bg-slate-200 dark:bg-slate-800" />
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
    </div>
  );
}
